import React, { useMemo, useState } from "react";
import styled from "styled-components";
import Search from "src/components/molecules/WorkRequestListViewContainer/components/Search";

type ServiceTypeOption = {
  id: string;
  label: string;
};

type ClassificationOption = {
  id: string;
  label: string;
};

interface ServiceTypeClassificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceTypes: ServiceTypeOption[];
  getClassifications: (serviceTypeId: string) => ClassificationOption[];
  selectedServiceTypeId?: string;
  onConfirm: (serviceTypeId: string, classificationId: string) => void;
}

export const ServiceTypeClassificationModal = ({
  isOpen,
  onClose,
  serviceTypes,
  getClassifications,
  selectedServiceTypeId = "",
  onConfirm,
}: ServiceTypeClassificationModalProps) => {
  const [serviceTypeId, setServiceTypeId] = useState(selectedServiceTypeId);
  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const effectiveSearch = (searchValue || appliedSearch).trim().toLowerCase();

  const sortedServiceTypes = useMemo(
    () => [...serviceTypes].sort((a, b) => a.label.localeCompare(b.label)),
    [serviceTypes],
  );

  const displayedServiceTypes = useMemo(() => {
    if (!effectiveSearch) return sortedServiceTypes;

    return sortedServiceTypes.filter((item) => {
      const id = item.id.toLowerCase();
      const label = item.label.toLowerCase();
      return id.includes(effectiveSearch) || label.includes(effectiveSearch);
    });
  }, [sortedServiceTypes, effectiveSearch]);

  if (!isOpen) return null;

  const closeAndReset = () => {
    setServiceTypeId(selectedServiceTypeId);
    setSearchValue("");
    setAppliedSearch("");
    onClose();
  };

  const handleServiceSelect = (id: string) => {
    setServiceTypeId(id);
    setSearchValue("");
    setAppliedSearch("");
  };

  const confirmSelection = () => {
    if (!serviceTypeId) return;

    const selectedClassificationId = getClassifications(serviceTypeId)[0]?.id || "";
    onConfirm(serviceTypeId, selectedClassificationId);
    closeAndReset();
  };

  const hasOptions = sortedServiceTypes.length > 0;

  return (
    <>
      <Overlay onClick={closeAndReset} />
      <Modal>
        <Header>
          <Title>Select Service Type</Title>
          <CloseButton type="button" onClick={closeAndReset}>
            ×
          </CloseButton>
        </Header>

        <Body>
          <SearchWrapper>
            <Search
              value={searchValue}
              applied={!!appliedSearch}
              onChange={setSearchValue}
              onClickApply={() => setAppliedSearch(searchValue.trim())}
              onClickCancel={() => setSearchValue(appliedSearch)}
              onClickClear={() => {
                setSearchValue("");
                setAppliedSearch("");
              }}
            />
          </SearchWrapper>

          <List>
            {hasOptions && displayedServiceTypes.length ? (
              displayedServiceTypes.map((item) => (
                <ListItem
                  key={item.id}
                  type="button"
                  $selected={serviceTypeId === item.id}
                  onClick={() => handleServiceSelect(item.id)}
                >
                  <ListItemText>{item.label}</ListItemText>
                  <ListItemChevron>›</ListItemChevron>
                </ListItem>
              ))
            ) : (
              <EmptyState>
                {hasOptions ? "No service types match your search." : "No options are available right now."}
              </EmptyState>
            )}
          </List>
        </Body>

        <Footer>
          <SecondaryButton type="button" onClick={closeAndReset}>
            Cancel
          </SecondaryButton>
          <PrimaryButton type="button" onClick={confirmSelection} disabled={!serviceTypeId}>
            Confirm Selection
          </PrimaryButton>
        </Footer>
      </Modal>
    </>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 999;
`;

const Modal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: min(720px, 96vw);
  max-height: 88vh;
  background: #ffffff;
  border: 1px solid #dde2e7;
  display: flex;
  flex-direction: column;
  z-index: 1000;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #dde2e7;
  padding: 16px 20px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2a37;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  font-size: 24px;
  line-height: 1;
  color: #6f7782;
  cursor: pointer;
`;

const Body = styled.div`
  padding: 16px 20px;
  overflow: auto;
`;

const SearchWrapper = styled.div`
  margin-bottom: 12px;

  > div {
    width: 100%;
  }
`;

const List = styled.div`
  border: 1px solid #dde2e7;
`;

const ListItem = styled.button<{ $selected: boolean }>`
  width: 100%;
  border: none;
  border-top: 1px solid #dde2e7;
  background: ${({ $selected }) => ($selected ? "#f3f4f6" : "#ffffff")};
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  cursor: pointer;
  text-align: left;

  &:first-child {
    border-top: none;
  }

  &:hover {
    background: #f9fafb;
  }
`;

const ListItemText = styled.span`
  font-size: 14px;
  color: #1f2a37;
`;

const ListItemChevron = styled.span`
  font-size: 18px;
  color: #c9d2dc;
`;

const EmptyState = styled.div`
  padding: 22px;
  text-align: center;
  color: #6f7782;
  font-size: 13px;
`;

const Footer = styled.div`
  border-top: 1px solid #dde2e7;
  padding: 12px 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const SecondaryButton = styled.button`
  border: 1px solid #c9d2dc;
  background: #ffffff;
  color: #1f2a37;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 10px 14px;
  font-weight: 600;
  cursor: pointer;
`;

const PrimaryButton = styled.button`
  border: 1px solid #1f2a37;
  background: #1f2a37;
  color: #ffffff;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 10px 14px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    background: #c9d2dc;
    border-color: #c9d2dc;
    cursor: not-allowed;
  }
`;
